import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 面会スケジュールテンプレート API 操作クラス
 */
export default class ReservationScheduleTemplates {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET ReservationScheduleTemplate
   */
  public async get(userStore: LoginUser, id: number): Promise<ReservationScheduleTemplateGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplate, formatParams);
    const response = await this.commonService.executeGet<ReservationScheduleTemplateGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET ReservationScheduleTemplates
   */
  public async gets(userStore: LoginUser): Promise<Array<ReservationScheduleTemplateGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplates, formatParams);
    const response = await this.commonService.executeGet<Array<ReservationScheduleTemplateGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET ReservationScheduleTemplatesCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplates, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST ReservationScheduleTemplate
   */
  public async post(userStore: LoginUser, requestBody: ReservationScheduleTemplatePostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplates, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH ReservationScheduleTemplate
   */
  public async patch(userStore: LoginUser, id: number, requestBody: ReservationScheduleTemplatePatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplate, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE ReservationScheduleTemplate
   */
  public async delete(userStore: LoginUser, id: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationScheduleTemplate, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface ReservationScheduleTemplateGetModel {
  /** 施設ID */
  facilityId: number;
  /** ID */
  id: number;

  /** テンプレート名 */
  name: string;
  /** 曜日情報 */
  weeks: Array<Weeks>;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface Weeks {
  /** 日曜 */
  sunday: boolean;
  /** 月曜 */
  monday: boolean;
  /** 火曜 */
  tuesday: boolean;
  /** 水曜 */
  wednesday: boolean;
  /** 木曜 */
  thursday: boolean;
  /** 金曜 */
  friday: boolean;
  /** 土曜 */
  saturday: boolean;

  /** 開始時間（時） */
  startHour: string;
  /** 開始時間（分） */
  startMin: string;
  /** 終了時間（時） */
  endHour: string;
  /** 終了時間（分） */
  endMin: string;
}

export interface ReservationScheduleTemplatePostModel {
  /** テンプレート名 */
  name: string;
  /** 曜日情報 */
  weeks: Array<Weeks>;
}

export interface ReservationScheduleTemplatePatchModel {
  /** テンプレート名 */
  name: string;
  /** 曜日情報 */
  weeks: Array<Weeks>;
  /** 更新日時 */
  updated: number;
}
